import { useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, ExternalLink } from 'lucide-react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getCustomerMailers } from '@/modules/Customer/services'
import type { CustomerMailer, MailerType } from '@/modules/Customer/types'

export interface ReportServiceSelectMeta {
    bindableCount: number
    requireBinding: boolean
}

interface Props {
    customerId?: number
    value?: number | null
    onChange: (serviceId: number | null) => void
    /** Called whenever bindableCount changes (after mailer data loads).
     *  requireBinding = true when bindableCount >= 2 (multi-mailer customer). */
    onMeta?: (meta: ReportServiceSelectMeta) => void
}

// Interactive picker for binding a report to one of the customer's
// services exposed via their `service.report`-scoped mailers. The V2
// worker (`Report#mailers`) uses the bound `service_id` to filter the
// Datacentral mailer search down to the right group when a customer
// has multiple report-mailer configurations.
//
// Source chain:
//   1. getCustomerMailers(customerId)
//   2. filter mailers where any `mailer_types[].mailer_scope === 'service.report'`
//   3. group: per-mailer → list its `services[]` (full service info from
//      the GET endpoint's JSON_AGG, see datacentral query
//      `GetCustomerMailers`).
//
// Empty states:
//   - !customerId            → "Pick customer first"
//   - no service.report mailer → "No mailer configured" + manage link
//   - mailer(s) with empty services → "Mailer not bound to any service"
const ReportServiceSelect = ({
    customerId,
    value,
    onChange,
    onMeta,
}: Props) => {
    const { t } = useTranslation('report_waf')

    const { data, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.REPORT_WAF, 'service-binding', customerId],
        queryFn: () => getCustomerMailers({ customerID: customerId! }),
        enabled: !!customerId && customerId !== 0,
    })

    // Mailers that carry the `service.report` scope (a mailer can have
    // multiple types; we match if ANY of its types is service.report).
    const reportMailers = useMemo<CustomerMailer[]>(() => {
        const all = data?.results || []
        return all.filter((m) =>
            m.mailer_types?.some(
                (mt: MailerType) => mt.mailer_scope === 'service.report'
            )
        )
    }, [data])

    // Mailers that have at least one bound service — used to determine
    // whether binding is required (>= 2 such mailers = multi-mailer customer).
    const bindableCount = useMemo(
        () => reportMailers.filter((m) => (m.services || []).length > 0).length,
        [reportMailers]
    )

    // Use a ref so the latest onMeta is always called without adding it
    // to the effect deps (avoids infinite loop when parent passes an
    // inline callback).
    const onMetaRef = useRef(onMeta)
    onMetaRef.current = onMeta

    useEffect(() => {
        onMetaRef.current?.({
            bindableCount,
            requireBinding: bindableCount >= 2,
        })
    }, [bindableCount])

    const manageHref = customerId
        ? `/customers/${customerId}?tab=mailers`
        : undefined

    if (!customerId || customerId === 0) {
        return (
            <Wrapper
                title={t('fields.bound_service', {
                    defaultValue: 'Bound service',
                })}
            >
                <p className="text-xs text-muted-foreground">
                    {t('bound_service.pick_customer', {
                        defaultValue:
                            'Pick a customer first — bindable services come from their service.report mailer.',
                    })}
                </p>
            </Wrapper>
        )
    }

    if (isLoading) {
        return (
            <Wrapper
                title={t('fields.bound_service', {
                    defaultValue: 'Bound service',
                })}
            >
                <p className="text-xs text-muted-foreground">
                    {t('common.loading', { defaultValue: 'Loading…' })}
                </p>
            </Wrapper>
        )
    }

    if (reportMailers.length === 0) {
        return (
            <Wrapper
                title={t('fields.bound_service', {
                    defaultValue: 'Bound service',
                })}
                manageHref={manageHref}
            >
                <p className="text-xs text-muted-foreground">
                    {t('bound_service.no_mailer', {
                        defaultValue:
                            'No service.report mailer configured for this customer — set one up at Customer > Mailers, then bind a service here.',
                    })}
                </p>
            </Wrapper>
        )
    }

    // Aggregate (mailer → services[]) for grouped display. A service
    // can technically appear in multiple mailers; dedupe by service_id
    // is handled visually by disabling the same id in subsequent groups
    // (radio behavior already enforces single selection).
    return (
        <Wrapper
            title={t('fields.bound_service', { defaultValue: 'Bound service' })}
            manageHref={manageHref}
            subtitle={t('bound_service.subtitle', {
                defaultValue:
                    'Pick the service whose mailer group should receive this report.',
            })}
        >
            <div className="space-y-3">
                {/* Hide "Unbound" escape when >= 2 bindable mailers exist —
                    multi-mailer customers must explicitly bind a service to
                    avoid non-deterministic delivery. */}
                {bindableCount < 2 && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => onChange(null)}
                            className={`text-xs rounded border px-2 py-1 transition-colors ${
                                value == null
                                    ? 'border-primary/50 bg-primary/10 text-primary'
                                    : 'border-border bg-background text-muted-foreground hover:bg-muted'
                            }`}
                        >
                            {t('bound_service.unbound', {
                                defaultValue: 'Unbound (legacy fallback)',
                            })}
                        </button>
                        <span className="text-xs text-muted-foreground">
                            {t('bound_service.unbound_hint', {
                                defaultValue:
                                    'No service binding — worker picks the first service.report mailer.',
                            })}
                        </span>
                    </div>
                )}

                {reportMailers.map((mailer) => {
                    const services = mailer.services || []
                    const recipientPreview = (mailer.mailer_receiver || '')
                        .split(/[,;]\s*/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .join(', ')

                    return (
                        <div
                            key={mailer.customer_mailer_id}
                            className="rounded border border-border bg-muted/30 p-2.5"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Mail className="w-3.5 h-3.5 shrink-0" />
                                <span className="text-sm font-medium">
                                    {mailer.customer_mailer_name ||
                                        `Mailer #${mailer.customer_mailer_id}`}
                                </span>
                                {recipientPreview && (
                                    <span className="text-xs text-muted-foreground truncate">
                                        — {recipientPreview}
                                        {(mailer.mailer_receiver || '')
                                            .split(/[,;]\s*/)
                                            .filter(Boolean).length > 2
                                            ? '…'
                                            : ''}
                                    </span>
                                )}
                            </div>

                            {services.length === 0 ? (
                                <p className="text-xs text-muted-foreground pl-5">
                                    {t('bound_service.mailer_unbound', {
                                        defaultValue:
                                            'Mailer is not bound to any service — bind a service in Customer > Mailers to make it pickable here.',
                                    })}
                                </p>
                            ) : (
                                <div className="flex flex-wrap gap-1.5 pl-5">
                                    {services.map((s: any) => {
                                        const id = Number(s.service_id)
                                        const checked = value === id
                                        return (
                                            <label
                                                key={id}
                                                className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs cursor-pointer transition-colors ${
                                                    checked
                                                        ? 'border-primary/50 bg-primary/10 text-primary'
                                                        : 'border-border bg-background hover:bg-muted'
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="report-service-binding"
                                                    className="sr-only"
                                                    checked={checked}
                                                    onChange={() =>
                                                        onChange(id)
                                                    }
                                                />
                                                <span>
                                                    {s.service_name || `#${id}`}
                                                </span>
                                                <span className="text-[10px] opacity-70">
                                                    #{id}
                                                </span>
                                            </label>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </Wrapper>
    )
}

const Wrapper = ({
    title,
    subtitle,
    manageHref,
    children,
}: {
    title: string
    subtitle?: string
    manageHref?: string
    children: React.ReactNode
}) => {
    const { t } = useTranslation('report_waf')
    return (
        <div className="rounded-md border border-border bg-muted/40 p-3">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="text-sm font-medium">{title}</span>
                    {subtitle && (
                        <span className="text-xs text-muted-foreground">
                            — {subtitle}
                        </span>
                    )}
                </div>
                {manageHref && (
                    <a
                        href={manageHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                        {t('recipients_manage_link', {
                            defaultValue: 'Manage at Customer > Mailers',
                        })}
                        <ExternalLink className="w-3 h-3" />
                    </a>
                )}
            </div>
            {children}
        </div>
    )
}

export default ReportServiceSelect
